/**
 * Centralized Date Utilities
 *
 * All date formatting functions use the Intl.DateTimeFormat API
 * with locale support for proper internationalization.
 *
 * Usage:
 *   import { formatTime, formatDate } from '@/utils/dateUtils'
 *   const { i18n } = useTranslation()
 *   const locale = i18n.language
 *   formatTime('2025-01-20T14:00:00Z', locale) // "2:00 PM" or "14:00"
 */

/**
 * Format time for display (e.g., "2:00 PM" or "14:00" depending on locale)
 */
export function formatTime(isoString: string, locale?: string): string {
  return new Date(isoString).toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Format time from HH:MM string (e.g., "14:00" -> "2:00 PM" depending on locale)
 */
export function formatTimeFromHHMM(time: string, locale?: string): string {
  const [hours, minutes] = time.split(':')
  const date = new Date()
  date.setHours(parseInt(hours), parseInt(minutes))
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

/**
 * Format date with full weekday, month, day, and year
 * e.g., "Monday, January 20, 2025" or "Montag, 20. Januar 2025"
 */
export function formatFullDate(isoString: string, locale?: string): string {
  return new Date(isoString).toLocaleDateString(locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Format date header without year (for grouped lists)
 * e.g., "Friday, January 24" or "Freitag, 24. Januar"
 */
export function formatDateHeader(isoDate: string, locale?: string): string {
  return new Date(isoDate).toLocaleDateString(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Format short date with abbreviated month
 * e.g., "Jan 20" or "20. Jan."
 */
export function formatShortDate(dateString: string, locale?: string): string {
  return new Date(dateString).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format short month name only
 * e.g., "Jan" or "Jan."
 */
export function formatMonth(dateString: string, locale?: string): string {
  return new Date(dateString).toLocaleDateString(locale, {
    month: 'short',
  })
}

/**
 * Format short weekday name
 * e.g., "Mon" or "Mo"
 */
export function formatWeekdayShort(dateString: string, locale?: string): string {
  return new Date(dateString).toLocaleDateString(locale, {
    weekday: 'short',
  })
}

/**
 * Format date and time together (medium format)
 * e.g., "Jan 20, 2025, 2:00 PM"
 */
export function formatDateTime(dateString: string, locale?: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(dateString))
}

/**
 * Format numeric date (compact)
 * e.g., "1/20/2025" or "20.1.2025"
 */
export function formatNumericDate(dateString: string, locale?: string): string {
  return new Date(dateString).toLocaleDateString(locale)
}

/**
 * Format date with month and day only (numeric)
 * e.g., "1/20" or "20.1."
 */
export function formatMonthDay(dateString: string, locale?: string): string {
  return new Date(dateString).toLocaleDateString(locale, {
    month: 'numeric',
    day: 'numeric',
  })
}

/**
 * Get day of month as number
 */
export function getDayOfMonth(dateString: string): number {
  return new Date(dateString).getDate()
}

/**
 * Get today's date as ISO string (YYYY-MM-DD)
 */
export function getTodayISO(): string {
  return new Date().toISOString().split('T')[0]
}

/**
 * Convert date string to Date object
 */
export function toDate(dateString: string): Date {
  return new Date(dateString)
}

/**
 * Check if a date is in the past
 */
export function isPast(dateString: string): boolean {
  return new Date(dateString) < new Date()
}

/**
 * Check if a date is in the future
 */
export function isFuture(dateString: string): boolean {
  return new Date(dateString) > new Date()
}

/**
 * Check if a date is today
 */
export function isToday(dateString: string): boolean {
  const date = new Date(dateString)
  const today = new Date()
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  )
}

/**
 * Check if a date is tomorrow
 */
export function isTomorrow(dateString: string): boolean {
  const date = new Date(dateString)
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return (
    date.getFullYear() === tomorrow.getFullYear() &&
    date.getMonth() === tomorrow.getMonth() &&
    date.getDate() === tomorrow.getDate()
  )
}

/**
 * Calculate duration in minutes between two ISO timestamps
 */
export function calculateDurationMinutes(startTime: string, endTime: string): number {
  const start = new Date(startTime).getTime()
  const end = new Date(endTime).getTime()
  return Math.round((end - start) / (1000 * 60))
}

/**
 * Format duration for display (English fallback)
 * e.g., "45 minutes", "1 hour", "1h 30min"
 * @deprecated Use formatDurationLocalized with a translation function for proper i18n
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} minutes`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  if (remainingMinutes === 0) return `${hours} hour${hours > 1 ? 's' : ''}`
  return `${hours}h ${remainingMinutes}min`
}

/**
 * Translation function type for formatDurationLocalized
 */
type TFunction = (key: string, options?: Record<string, unknown>) => string

/**
 * Format duration for display with i18n support
 * e.g., "45 minutes", "1 hour", "1h 30min" (localized)
 *
 * @param minutes - Duration in minutes
 * @param t - Translation function from useTranslation()
 */
export function formatDurationLocalized(minutes: number, t: TFunction): string {
  if (minutes < 60) {
    return t('common.duration.minutes', { count: minutes })
  }
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  if (remainingMinutes === 0) {
    return t('common.duration.hour', { count: hours })
  }
  return t('common.duration.hourMinutes', { hours, minutes: remainingMinutes })
}

/**
 * Check if date is within N days from now
 */
export function isWithinDays(dateString: string, days: number): boolean {
  const date = new Date(dateString)
  const now = new Date()
  const futureDate = new Date(now.getTime() + days * 24 * 60 * 60 * 1000)
  return date <= futureDate && date > now
}

/**
 * Sort dates ascending (nearest first)
 */
export function sortDatesAscending<T>(items: T[], getDate: (item: T) => string): T[] {
  return [...items].sort(
    (a, b) => new Date(getDate(a)).getTime() - new Date(getDate(b)).getTime()
  )
}

/**
 * Sort dates descending (most recent first)
 */
export function sortDatesDescending<T>(items: T[], getDate: (item: T) => string): T[] {
  return [...items].sort(
    (a, b) => new Date(getDate(b)).getTime() - new Date(getDate(a)).getTime()
  )
}

/**
 * Convert a date to ISO 8601 string with 3 decimal places for milliseconds
 * e.g., "2026-01-24T13:38:52.006Z"
 *
 * @param date - Optional Date object or ISO string. Defaults to current time if not provided.
 */
export function toISOStringWithMillis(date?: Date | string): string {
  const d = date ? (typeof date === 'string' ? new Date(date) : date) : new Date()
  return d.toISOString()
}
